import { useEffect, useState } from "react";
import client from "../lib/api";
import { Trophy, ChartBar } from "@phosphor-icons/react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

export const FollowupPayoffStrip = () => {
  const [payoff, setPayoff] = useState(null);
  const [outcomes, setOutcomes] = useState(null);

  useEffect(() => {
    client.get("/admin/followup-payoff").then((r) => setPayoff(r.data)).catch(() => {});
    client.get("/admin/reply-outcomes").then((r) => setOutcomes(r.data)).catch(() => {});
  }, []);

  const hasPayoff = payoff && (payoff.nudged > 0 || payoff.won_total > 0);
  const hasOutcomes = outcomes && outcomes.total_sent > 0;
  if (!hasPayoff && !hasOutcomes) return null;

  return (
    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="payoff-outcomes-strip">
      {hasPayoff && (
        <div className="card-surface p-4" data-testid="followup-payoff-card">
          <div className="flex items-center gap-2 mb-3">
            <Trophy size={14} weight="bold" color="#34d399" />
            <span className="overline">FOLLOW-UP PAYOFF</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="payoff-funnel">
            {[
              { label: "Nudged", value: payoff.nudged, testid: "payoff-nudged" },
              { label: "Replied", value: payoff.nudged_replied, testid: "payoff-replied" },
              { label: "Won", value: payoff.nudged_won, testid: "payoff-won" },
            ].map((step, i) => (
              <div key={step.label} className="flex items-center gap-2">
                {i > 0 && <span className="text-neutral-600 text-xs">→</span>}
                <div
                  data-testid={step.testid}
                  className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-center"
                >
                  <div className="font-mono text-xl font-semibold text-white leading-none">{step.value}</div>
                  <div className="text-[10px] text-neutral-500 uppercase tracking-wider mt-1">{step.label}</div>
                </div>
              </div>
            ))}
            <div
              data-testid="payoff-mrr"
              className="ml-auto rounded-lg border border-emerald-500/25 bg-emerald-500/[0.06] px-3 py-2 text-center"
            >
              <div className="font-mono text-xl font-semibold text-emerald-300 leading-none">
                ${(payoff.mrr_total || 0).toLocaleString()}
              </div>
              <div className="text-[10px] text-emerald-500/70 uppercase tracking-wider mt-1">
                /mo · {payoff.won_total} won
              </div>
            </div>
          </div>
          {payoff.monthly_revenue?.length > 0 && (
            <div className="mb-3" data-testid="deal-revenue-chart">
              <div className="text-[10px] text-neutral-500 uppercase tracking-wider mb-1">MRR over time</div>
              <div className="h-28">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={payoff.monthly_revenue} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="mrrFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#34d399" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#34d399" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="month" tick={{ fill: "#737373", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis
                      tick={{ fill: "#737373", fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                      width={42}
                      tickFormatter={(v) => `$${v}`}
                    />
                    <Tooltip
                      contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ color: "#999" }}
                      formatter={(v, name) => [
                        `$${Number(v).toLocaleString()}/mo`,
                        name === "cumulative_mrr" ? "Total MRR" : "Added",
                      ]}
                    />
                    <Area type="monotone" dataKey="cumulative_mrr" stroke="#34d399" strokeWidth={2} fill="url(#mrrFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {payoff.won_deals?.length > 0 && (
            <div className="space-y-1.5">
              {payoff.won_deals.slice(0, 4).map((d) => (
                <div
                  key={d.id}
                  data-testid={`won-deal-row-${d.id}`}
                  className="flex items-center gap-2 text-xs text-neutral-300"
                >
                  <span className="truncate flex-1 min-w-0">{d.company_name || d.contact_name}</span>
                  {d.was_nudged && (
                    <span className="rounded-full border border-sky-500/30 bg-sky-500/10 text-sky-300 px-1.5 py-0.5 text-[9px] font-mono uppercase">
                      nudged
                    </span>
                  )}
                  <span className="font-mono text-emerald-300 shrink-0">
                    {d.deal_value_monthly != null ? `$${d.deal_value_monthly.toLocaleString()}/mo` : "—"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {hasOutcomes && (
        <div className="card-surface p-4" data-testid="reply-outcomes-card">
          <div className="flex items-center gap-2 mb-3">
            <ChartBar size={14} weight="bold" color="#FF5A1F" />
            <span className="overline">BEST PITCH · {outcomes.total_sent} REPLIES SENT</span>
          </div>
          <div className="space-y-2.5">
            {outcomes.rows.map((r) => (
              <div key={r.template_id} data-testid={`outcome-row-${r.template_id}`} className="flex items-center gap-3">
                <div className="w-32 shrink-0 text-xs text-neutral-300 truncate">{r.label}</div>
                <div className="flex-1 min-w-0">
                  <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                    <div
                      className="h-full bg-[#FF5A1F] rounded-full transition-all duration-300"
                      style={{ width: `${r.reply_rate_pct}%` }}
                    />
                  </div>
                </div>
                <div className="w-14 shrink-0 text-right font-mono text-xs text-[#FF9764]">
                  {r.reply_rate_pct}%
                </div>
                <div className="w-20 shrink-0 text-right text-[10px] text-neutral-500 font-mono">
                  {r.sent} sent · {r.won} won
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
